/**
 * @fileoverview Reboot transport implementation for the differential client Store.
 *
 * Implements the Transport interface using Reboot's StoreV2 proto RPCs,
 * handling proto serialization/deserialization at the boundary.
 *
 * @module RebootTransport
 */
import { type JsonValue, type PartialMessage, Struct } from "@bufbuild/protobuf";
import type { ExternalContext } from "@reboot-dev/reboot";
import type { ChangeEntry, RawOperation, Row, Transport } from "differential/src/client/Transport";
import type { StoreSchema } from "differential/src/models/StoreSchema";

import type {
	ChangeEntry as ChangeEntryProto,
	Operation as OperationProto,
	StoreV2WeakReference,
} from "../../dist/dev/moment/differential/v1alpha2/store_rbt";

/**
 * Reboot transport for the differential client Store.
 *
 * Wraps a StoreV2 WeakReference and ExternalContext, implementing the
 * context-free Transport interface by capturing context at construction time.
 */
export class RebootTransport implements Transport {
	private readonly storeRef: StoreV2WeakReference;
	private readonly context: ExternalContext;
	private readonly schema: StoreSchema;

	/**
	 * @param storeRef - Reboot StoreV2 weak reference
	 * @param context - Reboot external context for RPC calls
	 * @param schema - Schema defining tables and their primary keys
	 */
	constructor(storeRef: StoreV2WeakReference, context: ExternalContext, schema: StoreSchema) {
		this.storeRef = storeRef;
		this.context = context;
		this.schema = schema;
	}

	async mutate(
		lastSeenVersion: number,
		operations: RawOperation[]
	): Promise<{ version: number; changes: ChangeEntry[] }> {
		const protoOps = operations.map((op) => operationToProto(op));
		const result = await this.storeRef.mutate(this.context, {
			lastSeenVersion: BigInt(lastSeenVersion),
			operations: protoOps,
		});
		const changes: ChangeEntry[] = result.changes.map(protoChangeToEntry);
		return { version: Number(result.version), changes };
	}

	async state(): Promise<{ version: number; tables: Record<string, Row[]> }> {
		const result = await this.storeRef.snapshot(this.context, {});
		const tables: Record<string, Row[]> = {};
		for (const [table, tableRows] of Object.entries(result.tables)) {
			tables[table] = tableRows.rows.map((s) => s.toJson() as Row);
		}
		return { version: Number(result.version), tables };
	}

	async changes(sinceVersion: number): Promise<{ version: number; changes: ChangeEntry[] }> {
		const result = await this.storeRef.changes(this.context, {
			sinceVersion: BigInt(sinceVersion),
		});
		const changes: ChangeEntry[] = result.changes.map(protoChangeToEntry);
		return { version: Number(result.version), changes };
	}
}

/**
 * Converts a RawOperation to a proto Operation partial.
 * @param op - Raw operation to convert
 * @returns Proto Operation partial message
 */
export function operationToProto(op: RawOperation): PartialMessage<OperationProto> {
	switch (op.type) {
		case "INSERT":
			return {
				op: {
					case: "insert",
					value: { table: op.table, value: structFromRecord(op.value) },
				},
			};
		case "DELETE":
			return {
				op: {
					case: "delete",
					value: { table: op.table, value: structFromRecord(op.value) },
				},
			};
		case "REPLACE":
			return {
				op: {
					case: "replace",
					value: {
						table: op.table,
						value: structFromRecord(op.value),
					},
				},
			};
		case "UPSERT":
			return {
				op: {
					case: "upsert",
					value: {
						table: op.table,
						value: structFromRecord(op.value),
					},
				},
			};
		case "CREATE_TABLE":
			return {
				op: {
					case: "createTable",
					value: { table: op.table, primaryKey: op.primaryKey },
				},
			};
		case "DROP_TABLE":
			return {
				op: {
					case: "dropTable",
					value: { table: op.table },
				},
			};
	}
}

/**
 * Converts a proto ChangeEntry to a client ChangeEntry.
 * @param proto - Proto change entry
 * @returns Client change entry
 */
export function protoChangeToEntry(proto: ChangeEntryProto): ChangeEntry {
	return {
		version: Number(proto.version),
		operation: protoOperationToRaw(proto.operation!),
	};
}

/**
 * Converts a proto Operation to a RawOperation (includes table).
 * @param proto - Proto operation
 * @returns Raw operation with table
 */
export function protoOperationToRaw(proto: OperationProto): RawOperation {
	switch (proto.op.case) {
		case "insert":
			return {
				type: "INSERT",
				table: proto.op.value.table,
				value: proto.op.value.value ? (proto.op.value.value.toJson() as Row) : {},
			};
		case "delete":
			return {
				type: "DELETE",
				table: proto.op.value.table,
				value: proto.op.value.value ? (proto.op.value.value.toJson() as Row) : {},
			};
		case "replace":
			return {
				type: "REPLACE",
				table: proto.op.value.table,
				value: proto.op.value.value ? (proto.op.value.value.toJson() as Row) : {},
			};
		case "upsert":
			return {
				type: "UPSERT",
				table: proto.op.value.table,
				value: proto.op.value.value ? (proto.op.value.value.toJson() as Row) : {},
			};
		case "createTable":
			return {
				type: "CREATE_TABLE",
				table: proto.op.value.table,
				primaryKey: proto.op.value.primaryKey,
			};
		case "dropTable":
			return {
				type: "DROP_TABLE",
				table: proto.op.value.table,
			};
		case undefined:
			throw new Error("Operation has no op set");
	}
}

/**
 * Creates a Struct from a plain record for proto serialization.
 * @param record - Plain record to convert
 * @returns Proto Struct
 */
function structFromRecord(record: Row): Struct {
	const clean = Object.fromEntries(Object.entries(record).filter(([, v]) => v !== undefined));
	return Struct.fromJson(clean as JsonValue);
}
